
import { toast } from "@/hooks/use-toast";
import { ConversionStage } from "../EvangelismAssistantProvider";
import { supabase } from "@/integrations/supabase/client";

type MessageType = {
  role: "human" | "ai";
  content: string;
};

interface EvangelismResponse {
  response: string;
  conversion_stage?: ConversionStage;
  softening_score?: number;
}

const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

export async function sendMessage(
  worldview: string,
  message: string,
  conversationHistory: Array<{ sender: "user" | "ai"; text: string }>,
  currentStage: ConversionStage,
  softeningScore: number = 0
): Promise<EvangelismResponse> {
  let retries = 0;
  
  // Limit conversation history to latest 8 messages to prevent overload
  const limitedHistory = conversationHistory.slice(-8);
  
  while (retries <= MAX_RETRIES) {
    try {
      console.log(`Sending message to evangelism-chat function (attempt ${retries + 1})`);
      
      const { data, error } = await supabase.functions.invoke('evangelism-chat', {
        body: {
          faith_group: worldview,
          question: message,
          conversation_history: limitedHistory.map(msg => ({
            sender: msg.sender,
            text: msg.text
          })),
          current_stage: currentStage,
          softening_score: softeningScore
        },
      });
      
      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(`Error: ${error.message}`);
      }

      // Log success for debugging
      console.log(`Successfully received response from evangelism chat service for ${worldview}`);
      
      if (!data) {
        throw new Error('Received empty response from server');
      }
      
      // Enhanced logging for debugging conversion status
      console.log(`Response data:`, data);
      console.log(`Conversion stage from API:`, data.conversion_stage);
      console.log(`Softening score from API:`, data.softening_score);
      
      return data as EvangelismResponse;
    } catch (error) {
      console.error(`Error fetching response (attempt ${retries + 1}):`, error);
      
      if (retries < MAX_RETRIES) {
        // Wait before retrying
        console.log(`Retrying in ${RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retries + 1)));
        retries++;
      } else {
        // All retries failed
        console.error("All retry attempts failed");
        
        let errorMessage = "There was a problem connecting to the evangelism chat service.";
        
        if (error instanceof Error) {
          if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError")) {
            errorMessage = "Network connection error. Please check your internet connection and try again.";
          } else if (error.message.includes("timeout")) {
            errorMessage = "The request timed out. Please try again later.";
          } else if (error.message.includes("rate limit") || error.message.includes("quota exceeded")) {
            errorMessage = "Service is currently experiencing high demand. Please try again in a few minutes.";
          }
        }
        
        toast({
          title: "Connection Error",
          description: errorMessage,
          variant: "destructive",
        });
        
        throw new Error(errorMessage);
      }
    }
  }
  
  // This should never be reached due to the throw in the final retry failure
  throw new Error("Failed to send message after multiple attempts");
}
